import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { LucyAvatar } from "./Lucy";
import { btnS } from "./BpKit";
import { dshort } from "./WeeklyPo";

const TAG = { behind_plan: "Behind plan", pre_po: "Pre-PO", overdue: "Overdue follow-up", quiet: "Not met lately" };

const Pick = ({ i, on, edit, toggle }) => (
  <li className="flex gap-2 py-1.5" data-testid={`lucy-draft-item-${i.item_id}`}>
    {edit && <input type="checkbox" checked={on} onChange={toggle} className="mt-1 accent-violet-600" data-testid={`lucy-draft-pick-${i.item_id}`} />}
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 text-sm">
        <span className={`font-medium ${on ? "" : "text-slate-400 line-through"}`}>{i.client}</span>
        <span className="num text-xs text-slate-500">{dshort(i.planned_date)}</span>
        {i.tags.map((t) => <span key={t} className="text-[10px] px-1.5 border border-violet-200 text-violet-700 rounded-sm">{TAG[t] || t}</span>)}
      </div>
      <div className="text-xs text-slate-600">{i.why}</div>
    </div>
  </li>
);

export const LucyDraft = ({ p, week, fy, onChange }) => {
  const d = p.draft;
  const [skip, setSkip] = useState({});
  const [busy, setBusy] = useState(false);
  const run = (req, ok) => { setBusy(true); req.then((r) => { ok(r.data); setSkip({}); onChange(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  const make = () => run(api.post("/lucy/meeting-draft", { user_id: p.user_id, week: week.key }, { params: { fy } }), (x) => toast.success(`Lucy suggested ${x.items.length} meeting(s)`));
  if (!d) {
    if (!p.can_edit || !p.can_draft || !p.drafts_left) return null;
    return (
      <button type="button" data-testid={`lucy-draft-make-${p.user_id}`} disabled={busy} onClick={make} className={`${btnS} inline-flex items-center mb-3 hover:border-violet-500`}>
        <Sparkles className={`h-4 w-4 mr-1 text-violet-600 ${busy ? "animate-pulse" : ""}`} />{busy ? "Lucy is drafting…" : "Draft with Lucy"}
      </button>
    );
  }
  const picked = d.items.filter((i) => !skip[i.item_id]);
  const accept = () => run(api.post(`/lucy/meeting-draft/${d.draft_id}/accept`, { item_ids: picked.map((i) => i.item_id) }), (x) => toast.success(`${x.added} meeting(s) added to the plan`));
  const dismiss = () => run(api.post(`/lucy/meeting-draft/${d.draft_id}/dismiss`), () => toast.success("Lucy's draft dismissed"));
  return (
    <div className="border border-violet-200 bg-violet-50/40 rounded-sm p-3 mb-3 ax-fade" data-testid={`lucy-draft-${p.user_id}`}>
      <div className="flex items-start gap-2">
        <LucyAvatar size="h-7 w-7 text-xs" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Lucy's suggested meetings</div>
          <div className="text-[11px] text-slate-500">{d.items.length} picked for {week.key} from accounts behind plan, open Pre-POs, overdue follow-ups and big accounts not met lately{d.source === "rules" ? " (picked by score; Lucy's notes weren't available)" : ""}</div>
        </div>
      </div>
      <ul className="mt-1 divide-y divide-violet-100">{d.items.map((i) => <Pick key={i.item_id} i={i} on={!skip[i.item_id]} edit={p.can_edit} toggle={() => setSkip((s) => ({ ...s, [i.item_id]: !s[i.item_id] }))} />)}</ul>
      {p.can_edit && (
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" data-testid={`lucy-draft-accept-${p.user_id}`} disabled={busy || !picked.length} onClick={accept}
            className="bg-violet-600 text-white h-9 px-4 text-sm rounded-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors">Accept {picked.length} meeting{picked.length === 1 ? "" : "s"}</button>
          {p.drafts_left > 0 && <button type="button" data-testid={`lucy-draft-redo-${p.user_id}`} disabled={busy} onClick={make} className={btnS} title={`${p.drafts_left} more draft(s) this week`}>Draft again</button>}
          <button type="button" data-testid={`lucy-draft-dismiss-${p.user_id}`} disabled={busy} onClick={dismiss} className="text-sm text-slate-500 hover:text-slate-900 px-2">Dismiss</button>
        </div>
      )}
    </div>
  );
};
